/**
 * Gives three parts of the merchant-written description a usable shape:
 * the colour photos, the dimensions, and the caveat list.
 *
 * The description lists one full-width photo per colour, so ten colours become
 * ten screens of scrolling on a phone. The same photos in a grid take about a
 * screen and a half, and let someone compare colours instead of remembering
 * them.
 *
 * The source markup is irregular - some images sit in a paragraph after the
 * heading, some inside the heading itself - so this walks the body in document
 * order and gives each image the last colour name it saw. If the shape is not
 * what it expects, it leaves the description exactly as it was.
 */
(() => {
  const BODY = '.product-description__body';
  const COLOUR = /^(.+?)\s+COLOU?RS?$/i;
  const MIN_PHOTOS = 3;

  const label = (raw) => {
    const name = raw.replace(COLOUR, '$1').trim().toLowerCase();
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const build = () => {
    const body = document.querySelector(BODY);
    if (!body || body.dataset.colourGrid === 'true') return;

    // Walk text and images together so an image inside a heading still picks
    // up the heading's own text.
    const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
    const found = [];
    let current = null;
    let firstHeading = null;
    let node;

    while ((node = walker.nextNode())) {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (COLOUR.test(text)) {
          current = label(text);
          // The run starts at the heading, not at the first photo, or the
          // heading is left stranded above the grid.
          if (!firstHeading) firstHeading = node;
        }
        continue;
      }
      // Photos after the last heading are still colour photos; they go in the
      // grid uncaptioned rather than being left full width below it. Guessing
      // a name for them would be worse than showing none.
      if (node.tagName === 'IMG' && (current || found.length)) {
        found.push({ img: node, name: current });
        current = null; // One photo per colour.
      }
    }

    if (found.length < MIN_PHOTOS || !firstHeading) return;

    const grid = document.createElement('ul');
    grid.className = 'colour-grid list-unstyled';
    grid.setAttribute('role', 'list');

    found.forEach(({ img, name }) => {
      const item = document.createElement('li');
      item.className = 'colour-grid__item';

      const picture = img.cloneNode(true);
      picture.className = 'colour-grid__image';
      picture.loading = 'lazy';
      picture.alt = name ? name + ' Dual Name Plank, with and without light' : '';
      picture.removeAttribute('width');
      picture.removeAttribute('height');

      const caption = document.createElement('span');
      caption.className = 'colour-grid__name';
      // Two photos carry no colour name in the source. The element is still
      // added, empty, so every card in the row is the same height; guessing a
      // name for them would be worse than showing none.
      caption.textContent = name || '';

      item.append(picture, caption);
      grid.append(item);
    });

    // Drop everything from the first colour heading to the last photo, and put
    // the grid in its place.
    const last = found[found.length - 1].img;
    const topOf = (el) => {
      let n = el;
      while (n.parentElement && n.parentElement !== body) n = n.parentElement;
      return n;
    };
    const start = topOf(firstHeading);
    const end = topOf(last);

    const doomed = [];
    for (let n = start; n; n = n.nextSibling) {
      doomed.push(n);
      if (n === end) break;
    }
    if (doomed[doomed.length - 1] !== end) return; // Unexpected order; leave it alone.

    const heading = document.createElement('h3');
    heading.className = 'spec-heading';
    heading.textContent = 'Colours';

    // Every photo is a pair, which the swipe row makes less obvious than ten
    // full-width photos did. Said once here instead of per card.
    const legend = document.createElement('p');
    legend.className = 'colour-grid__legend';
    legend.textContent =
      'Each photo shows the same plank twice: without the light on the left, with the light on the right.';

    body.insertBefore(heading, start);
    body.insertBefore(legend, start);
    body.insertBefore(grid, start);
    doomed.forEach((n) => n.remove());

    // The colour run ends on an empty paragraph in the source. Left in place
    // it reads as a gap someone forgot to fill.
    const trailing = grid.nextElementSibling;
    if (trailing && !trailing.textContent.trim() && !trailing.querySelector('img, iframe, video')) {
      trailing.remove();
    }

    body.dataset.colourGrid = 'true';
  };

  /**
   * The two sizes are written as six loose lines. They are a comparison, so
   * they belong in a table: three measurements against two options, with the
   * one that actually differs (height) visible at a glance.
   */
  const DIMS = /(?:^|\n)\s*Without light\s*\n\s*Length\s*[-–]\s*([\d.]+\s*cm)\s*\n\s*Width\s*[-–]\s*([\d.]+\s*cm)\s*\n\s*Height\s*[-–]\s*([\d.]+\s*cm)[\s\S]*?With light\s*\n\s*Length\s*[-–]\s*([\d.]+\s*cm)\s*\n\s*Width\s*[-–]\s*([\d.]+\s*cm)\s*\n\s*Height\s*[-–]\s*([\d.]+\s*cm)/i;

  const buildDimensions = () => {
    const body = document.querySelector(BODY);
    if (!body || body.dataset.dimsTable === 'true') return;

    const kids = Array.from(body.children);
    const start = kids.findIndex((el) => /^\s*DIME?S?N?S?IONS?\s*:?\s*$/i.test(el.textContent.trim()));
    if (start < 0) return;

    // Gather the blocks after the heading until the measurements are complete.
    let text = '';
    let end = start;
    for (let i = start + 1; i < kids.length && i <= start + 6; i += 1) {
      text += '\n' + (kids[i].innerText || '');
      end = i;
      if (DIMS.test(text)) break;
    }
    const m = text.match(DIMS);
    if (!m) return;

    const [, l1, w1, h1, l2, w2, h2] = m;
    const table = document.createElement('table');
    table.className = 'spec-table';
    table.innerHTML =
      '<thead><tr><th scope="col"><span class="visually-hidden">Measurement</span></th>' +
      '<th scope="col">Without light</th><th scope="col">With light</th></tr></thead>' +
      '<tbody>' +
      ['Length', 'Width', 'Height'].map((name, i) =>
        '<th scope="row">' + name + '</th><td>' + [l1, w1, h1][i] + '</td><td>' + [l2, w2, h2][i] + '</td>'
      ).map((row) => '<tr>' + row + '</tr>').join('') +
      '</tbody>';

    // The heading carries a typo in the source; it is display text, so correct
    // it here rather than leave "DIMESNIONS" on the page.
    kids[start].textContent = 'Dimensions';
    kids[start].classList.add('spec-heading');

    body.insertBefore(table, kids[start + 1]);
    for (let i = start + 1; i <= end; i += 1) kids[i].remove();
    body.dataset.dimsTable = 'true';
  };

  /**
   * The caveats are one block of text with hyphens standing in for bullets,
   * which renders as a wall. They are the terms someone agrees to on a
   * non-returnable order, so they need to be readable.
   */
  const buildNotes = () => {
    const body = document.querySelector(BODY);
    if (!body || body.dataset.noteList === 'true') return;

    const host = Array.from(body.children).find(
      (el) => /PLEASE NOTE/i.test(el.textContent) && el.textContent.length > 120
    );
    if (!host) return;

    const lines = (host.innerText || '').split('\n').map((l) => l.trim()).filter(Boolean);
    const items = [];
    let closing = '';
    lines.forEach((line) => {
      if (/^PLEASE NOTE/i.test(line)) return;
      if (/^HOW TO ORDER/i.test(line)) return;
      if (/^[-–]\s*/.test(line)) {
        items.push(line.replace(/^[-–]\s*/, ''));
      } else if (/FINAL PRODUCT MAY VARY/i.test(line)) {
        closing = line;
      }
    });
    if (items.length < 3) return;

    const wrap = document.createElement('div');
    wrap.className = 'spec-notes';

    const heading = document.createElement('h3');
    heading.className = 'spec-heading';
    heading.textContent = 'Please note';

    const list = document.createElement('ul');
    list.className = 'spec-notes__list';
    items.forEach((text) => {
      const li = document.createElement('li');
      li.textContent = text.replace(/\s+/g, ' ').trim();
      list.append(li);
    });

    wrap.append(list);

    if (closing) {
      const p = document.createElement('p');
      p.className = 'spec-notes__closing';
      // Shouted in the source; it reads as a caution either way.
      p.textContent =
        closing.charAt(0).toUpperCase() + closing.slice(1).toLowerCase().replace(/\s+/g, ' ');
      wrap.append(p);
    }

    host.replaceWith(heading, wrap);
    body.dataset.noteList = 'true';
  };

  const enhance = () => {
    build();
    buildDimensions();
    buildNotes();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhance, { once: true });
  } else {
    enhance();
  }
})();
